import { fold } from "../fable_modules/fable-library-js.4.29.0/Seq.js";
import { Option_defaultOrMap, Enum_bitSetOrder } from "./Prelude.js";
import { array_type, tuple_type, union_type, string_type, option_type, float64_type, bool_type, record_type, uint32_type, enum_type, int32_type, getEnumName } from "../fable_modules/fable-library-js.4.29.0/Reflection.js";
import { op_UnaryNegation_Int32 } from "../fable_modules/fable-library-js.4.29.0/Int32.js";
import { contains, copy, sum, updateAt, item as item_2, setItem, fill, initialize } from "../fable_modules/fable-library-js.4.29.0/Array.js";
import { Union, Record } from "../fable_modules/fable-library-js.4.29.0/Types.js";
import { min } from "../fable_modules/fable-library-js.4.29.0/Double.js";
import { Profession, SkillsModule_professionActive, Qualities_mult, Qualities_zero, Qualities$1 } from "./Skills.js";
import { numberHash, round } from "../fable_modules/fable-library-js.4.29.0/Util.js";
import { ProcessorModule_seedMakerAccepts } from "./Items.js";

export function SeasonsModule_contains(season, seasons) {
    return !(((1 << season) & seasons) === 0);
}

export function SeasonsModule_ofSeq(seasons) {
    return fold((seasons_1, season) => (seasons_1 | (1 << season)), 0, seasons);
}

export function SeasonsModule_setOrder(a, b) {
    return Enum_bitSetOrder(a, b);
}

export function SeasonModule_name(season) {
    return getEnumName(enum_type("StardewValleyStonks.Season", int32_type, [["Spring", 0], ["Summer", 1], ["Fall", 2], ["Winter", 3]]), season);
}

function SeasonModule_wrapAroundNegative(value) {
    if (value < 0) {
        return (value + 4) | 0;
    }
    else {
        return value | 0;
    }
}

function SeasonModule_plusMod(i, season) {
    return (season + i) % 4;
}

function SeasonModule_minus(i, season) {
    return SeasonModule_wrapAroundNegative(SeasonModule_plusMod(op_UnaryNegation_Int32(i), season));
}

export function SeasonModule_next(season) {
    return SeasonModule_plusMod(1, season);
}

export function SeasonModule_previous(season) {
    return SeasonModule_minus(1, season);
}

export function SeasonModule_seasonsBetween(start, finish) {
    let seasons = 0;
    let season = start;
    while (season !== finish) {
        seasons = ((seasons | (1 << season)) | 0);
        season = (SeasonModule_next(season) | 0);
    }
    return (seasons | (1 << finish)) | 0;
}

export function SeasonModule_distance(start, finish) {
    return SeasonModule_wrapAroundNegative(finish - start);
}

export function SeasonModule_span(start, finish) {
    return initialize(SeasonModule_distance(start, finish) + 1, (i) => SeasonModule_plusMod(i, start), Int32Array);
}

export class Date$ extends Record {
    constructor(Season, Day) {
        super();
        this.Season = (Season | 0);
        this.Day = Day;
    }
}

export function Date$_$reflection() {
    return record_type("StardewValleyStonks.Date", [], Date$, () => [["Season", enum_type("StardewValleyStonks.Season", int32_type, [["Spring", 0], ["Summer", 1], ["Fall", 2], ["Winter", 3]])], ["Day", uint32_type]]);
}

export function DateModule_daySpan(start, finish) {
    const days = fill(new Uint32Array(SeasonModule_distance(start.Season, finish.Season) + 1), 0, SeasonModule_distance(start.Season, finish.Season) + 1, 28);
    setItem(days, 0, (28 - start.Day) + 1);
    setItem(days, days.length - 1, item_2(days.length - 1, days) - (28 - finish.Day));
    return days;
}

export function DateModule_totalDays(start, finish) {
    let value;
    return (((((value = (SeasonModule_distance(start.Season, finish.Season) | 0), value >>> 0)) * 28) - start.Day) + finish.Day) + 1;
}

export function ToolLevelModule_name(toolLevel) {
    return getEnumName(enum_type("StardewValleyStonks.ToolLevel", int32_type, [["Normal", 0], ["Copper", 1], ["Steel", 2], ["Gold", 3], ["Iridium", 4]]), toolLevel);
}

export class CropAmountSettings extends Record {
    constructor(SpecialCharm, LuckBuff, GiantCropOrientationsPerTile, ShavingToolLevel) {
        super();
        this.SpecialCharm = SpecialCharm;
        this.LuckBuff = LuckBuff;
        this.GiantCropOrientationsPerTile = GiantCropOrientationsPerTile;
        this.ShavingToolLevel = ShavingToolLevel;
    }
}

export function CropAmountSettings_$reflection() {
    return record_type("StardewValleyStonks.CropAmountSettings", [], CropAmountSettings, () => [["SpecialCharm", bool_type], ["LuckBuff", uint32_type], ["GiantCropOrientationsPerTile", float64_type], ["ShavingToolLevel", option_type(enum_type("StardewValleyStonks.ToolLevel", int32_type, [["Normal", 0], ["Copper", 1], ["Steel", 2], ["Gold", 3], ["Iridium", 4]]))]]);
}

export const CropAmountSettingsModule_common = new CropAmountSettings(false, 0, 3, undefined);

export class CropAmount extends Record {
    constructor(MinCropYield, MaxCropYield, ExtraCropChance, CanDouble, FarmLevelsPerYieldIncrease, FarmingQualities) {
        super();
        this.MinCropYield = MinCropYield;
        this.MaxCropYield = MaxCropYield;
        this.ExtraCropChance = ExtraCropChance;
        this.CanDouble = CanDouble;
        this.FarmLevelsPerYieldIncrease = FarmLevelsPerYieldIncrease;
        this.FarmingQualities = FarmingQualities;
    }
}

export function CropAmount_$reflection() {
    return record_type("StardewValleyStonks.CropAmount", [], CropAmount, () => [["MinCropYield", uint32_type], ["MaxCropYield", uint32_type], ["ExtraCropChance", float64_type], ["CanDouble", bool_type], ["FarmLevelsPerYieldIncrease", uint32_type], ["FarmingQualities", bool_type]]);
}

export const CropAmountModule_singleAmount = new CropAmount(1, 1, 0, true, 0, true);

export function CropAmountModule_doubleHarvestProb(settings) {
    return (((settings.SpecialCharm ? 0.025 : 0) / 1200) + (settings.LuckBuff / 1500)) + 0.0001;
}

export function CropAmountModule_expectedQuantityWithDoubling(settings, quantity) {
    return (CropAmountModule_doubleHarvestProb(settings) * quantity) + quantity;
}

export function CropAmountModule_expectedExtraCropQuantity(extraChance) {
    return (1 / (1 - min(extraChance, 0.9))) - 1;
}

export function CropAmountModule_expectedQuantity(skills, settings, amount) {
    const expectedQuantity = (((amount.MinCropYield + amount.MaxCropYield) + (((amount.FarmLevelsPerYieldIncrease !== 0) && ((amount.MinCropYield > 1) ? true : (amount.MaxCropYield > 1))) ? ~~(skills.Farming.Level / amount.FarmLevelsPerYieldIncrease) : 0)) / 2) + CropAmountModule_expectedExtraCropQuantity(amount.ExtraCropChance);
    if (amount.CanDouble) {
        return CropAmountModule_expectedQuantityWithDoubling(settings, expectedQuantity);
    }
    else {
        return expectedQuantity;
    }
}

export function CropAmountModule_expectedQuantityByQuality(skills, settings, cropAmount, farmingQuantities) {
    const quantity = CropAmountModule_expectedQuantity(skills, settings, cropAmount);
    if (cropAmount.FarmingQualities) {
        const qualities_1 = farmingQuantities;
        return new Qualities$1(updateAt(0, (quantity - 1) + item_2(0, qualities_1.fields[0]), qualities_1.fields[0], Float64Array));
    }
    else {
        return new Qualities$1(updateAt(0, quantity + item_2(0, Qualities_zero.fields[0]), Qualities_zero.fields[0], Float64Array));
    }
}

export function CropAmountModule_expectedQuantityFromGiantCropShaving(shavingToolLevel) {
    const damage = (~~(shavingToolLevel / 2) + 1) | 0;
    const shavingProb = damage / 5;
    return ((~~(3 / damage) * shavingProb) * (2 * damage)) + (shavingProb * (2 * (3 % damage)));
}

export function CropAmountModule_expectedGiantCropYield(settings) {
    return 2 + Option_defaultOrMap(0, CropAmountModule_expectedQuantityFromGiantCropShaving, settings.ShavingToolLevel);
}

export function CropAmountModule_noGiantCropProb(settings) {
    const arg0__1 = 1 - 0.01;
    return Math.pow(arg0__1, settings.GiantCropOrientationsPerTile);
}

export function CropAmountModule_expectedGiantQuantity(skills, settings, amount) {
    const nonGiantQuantity = CropAmountModule_expectedQuantity(skills, settings, amount);
    const giantQuantity = CropAmountModule_expectedGiantCropYield(settings);
    const noGiantProb = CropAmountModule_noGiantCropProb(settings);
    return (noGiantProb * nonGiantQuantity) + ((1 - noGiantProb) * giantQuantity);
}

export function CropAmountModule_expectedGiantQuantityByQuality(skills, settings, amount, farmingQuantities) {
    const noGiantProb = CropAmountModule_noGiantCropProb(settings);
    const expectedGiant = (1 - noGiantProb) * CropAmountModule_expectedGiantCropYield(settings);
    const qualities_2 = Qualities_mult(noGiantProb, CropAmountModule_expectedQuantityByQuality(skills, settings, amount, farmingQuantities));
    return new Qualities$1(updateAt(0, expectedGiant + item_2(0, qualities_2.fields[0]), qualities_2.fields[0], Float64Array));
}

export class SeedPrice extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["FixedPrice", "ScalingPrice"];
    }
}

export function SeedPrice_$reflection() {
    return union_type("StardewValleyStonks.SeedPrice", [], SeedPrice, () => [[["Item1", string_type], ["Item2", uint32_type]], [["Item1", string_type], ["Item2", option_type(uint32_type)]]]);
}

export function SeedPriceModule_vendor(_arg) {
    let v;
    if (_arg.tag === 1) {
        v = _arg.fields[0];
    }
    else {
        v = _arg.fields[0];
    }
    return v;
}

export function Growth_stagesAndTime(speedBonus, stages) {
    const total = sum(stages, {
        GetZero: () => 0,
        Add: (x, y) => (x + y),
    });
    const stages_1 = copy(stages);
    let daysToReduce;
    let value_1;
    const value = Math.fround(speedBonus) * total;
    value_1 = Math.ceil(value);
    daysToReduce = (value_1 >>> 0);
    let daysReduced = 0;
    let traverses = 0;
    while ((daysReduced < daysToReduce) && (traverses < 3)) {
        if (item_2(0, stages_1) > 1) {
            setItem(stages_1, 0, item_2(0, stages_1) - 1);
            daysReduced = (daysReduced + 1);
        }
        let stage = 1;
        while ((daysReduced < daysToReduce) && (stage < stages_1.length)) {
            if (item_2(stage, stages_1) > 0) {
                setItem(stages_1, stage, item_2(stage, stages_1) - 1);
                daysReduced = (daysReduced + 1);
            }
            stage = ((stage + 1) | 0);
        }
        traverses = (traverses + 1);
    }
    return [stages_1, total - daysReduced];
}

export function Growth_maxHarvests(regrowTime, growthTime, days) {
    const days_1 = days - 1;
    if (regrowTime == null) {
        return ~~(days_1 / growthTime) | 0;
    }
    else if (growthTime > days_1) {
        return 0;
    }
    else {
        const time = regrowTime;
        return 1 + ~~((days_1 - growthTime) / time);
    }
}

export function Growth_daysNeededFor(regrowTime, growthTime, harvests) {
    if (regrowTime == null) {
        return growthTime * harvests;
    }
    else {
        return growthTime + (regrowTime * (harvests - 1));
    }
}

export class FarmCrop extends Record {
    constructor(Seed, Item, Amount, ExtraItem, Giant, Seasons, Stages, RegrowTime, Paddy) {
        super();
        this.Seed = Seed;
        this.Item = Item;
        this.Amount = Amount;
        this.ExtraItem = ExtraItem;
        this.Giant = Giant;
        this.Seasons = (Seasons | 0);
        this.Stages = Stages;
        this.RegrowTime = RegrowTime;
        this.Paddy = Paddy;
    }
}

export function FarmCrop_$reflection() {
    return record_type("StardewValleyStonks.FarmCrop", [], FarmCrop, () => [["Seed", uint32_type], ["Item", uint32_type], ["Amount", CropAmount_$reflection()], ["ExtraItem", option_type(tuple_type(uint32_type, float64_type))], ["Giant", bool_type], ["Seasons", enum_type("StardewValleyStonks.Seasons", int32_type, [["None", 0], ["Spring", 1], ["Summer", 2], ["Fall", 4], ["Winter", 8], ["All", 15]])], ["Stages", array_type(uint32_type)], ["RegrowTime", option_type(uint32_type)], ["Paddy", bool_type]]);
}

export function FarmCropModule_seedItem(crop) {
    return crop.Seed * 1;
}

export function FarmCropModule_growsInSeason(season, crop) {
    return SeasonsModule_contains(season, crop.Seasons);
}

export function FarmCropModule_growsInSeasons(seasons, crop) {
    return !((seasons & crop.Seasons) === 0);
}

export function FarmCropModule_xpPerItem(item, crop) {
    const price = item(crop.Item).SellPrice;
    const value_2 = round(16 * Math.log((0.018 * price) + 1));
    return value_2 >>> 0;
}

export function FarmCropModule_xpItemsPerHarvest(giantCropProb, crop) {
    if (crop.Giant) {
        return 1 - giantCropProb;
    }
    else {
        return 1;
    }
}

export function FarmCropModule_xpPerHarvest(item, giantCropProb, crop) {
    return FarmCropModule_xpPerItem(item, crop) * FarmCropModule_xpItemsPerHarvest(giantCropProb, crop);
}

export function FarmCropModule_items(crop) {
    const matchValue = crop.ExtraItem;
    if (matchValue == null) {
        return new Uint32Array([crop.Item]);
    }
    else {
        return new Uint32Array([crop.Item, matchValue[0]]);
    }
}

export class ForageCrop extends Record {
    constructor(Seed, Foragables, SeedRecipeUnlockLevel, Season, Stages) {
        super();
        this.Seed = Seed;
        this.Foragables = Foragables;
        this.SeedRecipeUnlockLevel = SeedRecipeUnlockLevel;
        this.Season = (Season | 0);
        this.Stages = Stages;
    }
}

export function ForageCrop_$reflection() {
    return record_type("StardewValleyStonks.ForageCrop", [], ForageCrop, () => [["Seed", uint32_type], ["Foragables", array_type(uint32_type)], ["SeedRecipeUnlockLevel", uint32_type], ["Season", enum_type("StardewValleyStonks.Season", int32_type, [["Spring", 0], ["Summer", 1], ["Fall", 2], ["Winter", 3]])], ["Stages", array_type(uint32_type)]]);
}

export function ForageCropModule_seedItem(crop) {
    return crop.Seed * 1;
}

export function ForageCropModule_name(item, crop) {
    return item(crop.Seed * 1).Name;
}

export function ForageCropModule_seedRecipeUnlocked(skills, crop) {
    return skills.Foraging.Level >= crop.SeedRecipeUnlockLevel;
}

export function ForageCropModule_seasons(crop) {
    return 1 << crop.Season;
}

export function ForageCropModule_growsInSeason(season, crop) {
    return crop.Season === season;
}

export function ForageCropModule_growsInSeasons(seasons, crop) {
    return SeasonsModule_contains(crop.Season, seasons);
}

export function ForageCropModule_xpItemsPerHarvest(skills) {
    if (SkillsModule_professionActive(new Profession(3, []), skills)) {
        return 1.2;
    }
    else {
        return 1;
    }
}

export function ForageCropModule_xpPerHarvest(skills) {
    return 5 * ForageCropModule_xpItemsPerHarvest(skills);
}

export class Crop extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["FarmCrop", "ForageCrop"];
    }
}

export function Crop_$reflection() {
    return union_type("StardewValleyStonks.Crop", [], Crop, () => [[["Item", FarmCrop_$reflection()]], [["Item", ForageCrop_$reflection()]]]);
}

export function CropModule_name(item, _arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_name(item, _arg.fields[0]);
    }
    else {
        return item(_arg.fields[0].Item).Name;
    }
}

export function CropModule_seasons(_arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_seasons(_arg.fields[0]) | 0;
    }
    else {
        return _arg.fields[0].Seasons | 0;
    }
}

export function CropModule_growsInSeason(season, _arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_growsInSeason(season, _arg.fields[0]);
    }
    else {
        return FarmCropModule_growsInSeason(season, _arg.fields[0]);
    }
}

export function CropModule_growsInSeasons(seasons, _arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_growsInSeasons(seasons, _arg.fields[0]);
    }
    else {
        return FarmCropModule_growsInSeasons(seasons, _arg.fields[0]);
    }
}

export function CropModule_stages(_arg) {
    if (_arg.tag === 1) {
        return _arg.fields[0].Stages;
    }
    else {
        return _arg.fields[0].Stages;
    }
}

export function CropModule_growthTime(crop) {
    return sum(CropModule_stages(crop), {
        GetZero: () => 0,
        Add: (x, y) => (x + y),
    });
}

export function CropModule_paddy(_arg) {
    if (_arg.tag === 1) {
        return false;
    }
    else {
        return _arg.fields[0].Paddy;
    }
}

export function CropModule_giant(_arg) {
    if (_arg.tag === 1) {
        return false;
    }
    else {
        return _arg.fields[0].Giant;
    }
}

export function CropModule_mainItem(_arg) {
    if (_arg.tag === 1) {
        return item_2(0, _arg.fields[0].Foragables);
    }
    else {
        return _arg.fields[0].Item;
    }
}

export function CropModule_items(_arg) {
    if (_arg.tag === 1) {
        return _arg.fields[0].Foragables;
    }
    else {
        return FarmCropModule_items(_arg.fields[0]);
    }
}

export function CropModule_seed(_arg) {
    if (_arg.tag === 1) {
        return _arg.fields[0].Seed;
    }
    else {
        return _arg.fields[0].Seed;
    }
}

export function CropModule_seedItem(_arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_seedItem(_arg.fields[0]);
    }
    else {
        return FarmCropModule_seedItem(_arg.fields[0]);
    }
}

export function CropModule_growthTimeWith(speed, crop) {
    return Growth_stagesAndTime(speed, CropModule_stages(crop))[1];
}

export function CropModule_growthStagesAndTime(speed, crop) {
    return Growth_stagesAndTime(speed, CropModule_stages(crop));
}

export function CropModule_regrowTime(_arg) {
    if (_arg.tag === 1) {
        return undefined;
    }
    else {
        return _arg.fields[0].RegrowTime;
    }
}

export const CropModule_regrows = (arg) => (CropModule_regrowTime(arg) != null);

export function CropModule_xpPerItem(item, _arg) {
    if (_arg.tag === 1) {
        return 5;
    }
    else {
        return FarmCropModule_xpPerItem(item, _arg.fields[0]);
    }
}

export function CropModule_xpItemsPerHarvest(giantCropProb, skills, _arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_xpItemsPerHarvest(skills);
    }
    else {
        return FarmCropModule_xpItemsPerHarvest(giantCropProb, _arg.fields[0]);
    }
}

export function CropModule_xpPerHarvest(item, giantCropProb, skills, _arg) {
    if (_arg.tag === 1) {
        return ForageCropModule_xpPerHarvest(skills);
    }
    else {
        return FarmCropModule_xpPerHarvest(item, giantCropProb, _arg.fields[0]);
    }
}

export function CropModule_isFarm(_arg) {
    if (_arg.tag === 1) {
        return false;
    }
    else {
        return true;
    }
}

export function CropModule_isForage(_arg) {
    if (_arg.tag === 1) {
        return true;
    }
    else {
        return false;
    }
}

export function CropModule_canGetOwnSeedsFromSeedMaker(item, crop) {
    return ProcessorModule_seedMakerAccepts(item(CropModule_mainItem(crop)));
}

export function CropModule_makesOwnSeeds(crop) {
    const array = CropModule_items(crop);
    return contains(CropModule_seedItem(crop), array, {
        Equals: (x, y) => (x === y),
        GetHashCode: numberHash,
    });
}

//# sourceMappingURL=Crops.js.map
